import translation from 'locales/translation';
import { FC, Fragment, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { PautaEvento } from 'api/Assessor/pautas';

import { Button, IconButton, Typography } from '@material-ui/core';
import {
  AgencyIcon,
  BlockIcon,
  CircleCheck,
  CircleTimes,
  DropdownIcon,
  LegalPersonIcon,
  LockIcon,
  PeopleIcon,
  RestritoIcon,
} from 'components/Icons';
import ComissaoEleitoralModal, {
  ComissaoEleitoralProps,
} from 'components/Modal/__LEGACY__/ComissaoEleitoralModal';
import {
  AssembleiaPautaStatus,
  AssembleiaTipoPauta,
} from 'helpers/enums/AssembleiaPautaStatus';

import { modalsActionsOpen } from 'store/modules/ModalsActions';

import { getUniqueKey } from 'helpers/utils/KeyUtils';

import useStyle from './style';

interface TabProps {
  pauta: PautaEvento;
  idEvento: number;
}

export const Tab: FC<TabProps> = ({ pauta, idEvento }) => {
  const classes = useStyle();
  const dispatch = useDispatch();

  const openModalComissaoEleitoral = useCallback(() => {
    const modalKey: string = getUniqueKey();
    dispatch(
      modalsActionsOpen(
        ComissaoEleitoralModal,
        {
          genericProps: {
            key: modalKey,
            idEvento,
            isAssessor: true,
          } as ComissaoEleitoralProps,
        },
        modalKey
      )
    );
  }, [dispatch, idEvento]);

  if (pauta.tipoPauta === AssembleiaTipoPauta.Coordenador) {
    return (
      <Fragment>
        <Typography component='label' className={classes.tabPautaInfo}>
          <AgencyIcon />
          {translation.AssembleiaPauta.tabPauta.agencia(pauta.codigoAgencia)}
        </Typography>
        <Typography component='label' className={classes.tabPautaInfo}>
          <PeopleIcon />
          {translation.AssembleiaPauta.tabPauta.nucleo(pauta.numeroNucleo)}
        </Typography>
      </Fragment>
    );
  }
  if (pauta.tipoPauta === AssembleiaTipoPauta.Eleicao) {
    return (
      <Button
        variant='text'
        color='primary'
        startIcon={<LegalPersonIcon />}
        onClick={openModalComissaoEleitoral}
      >
        {translation.AssembleiaPauta.tabPauta.comissao}
      </Button>
    );
  }
  return null;
};

export const Caption: FC = () => {
  const classes = useStyle();

  return (
    <label className={classes.pautasCaption}>
      <span>{translation.AssembleiaAssociadoRoute.pautas.legenda.title}</span>
      <BlockIcon />
      <span>{translation.AssembleiaAssociadoRoute.pautas.legenda.secreta}</span>
      <RestritoIcon />
      <span>
        {translation.AssembleiaAssociadoRoute.pautas.legenda.restrita}
      </span>
    </label>
  );
};

interface PaginacaoProps {
  total: number;
  current: number;
  onChange: (value: number) => void;
  type?: 'default' | 'text';
}

export const Paginacao: FC<PaginacaoProps> = ({
  total,
  current,
  onChange,
  type = 'default',
}) => {
  const classes = useStyle();

  const pages = useMemo(() => {
    const arr = [];
    let current = 0;
    while (arr.length < total) {
      current += 1;
      arr.push(current);
    }
    return arr;
  }, [total]);

  return (
    <nav className={classes.paginacao}>
      {total > 0 && current > 1 && (
        <IconButton onClick={() => onChange(current - 1)} color='primary'>
          <DropdownIcon className={classes.paginacaoPrev} />
        </IconButton>
      )}
      {type === 'text' && (
        <Typography
          dangerouslySetInnerHTML={{
            __html: String(
              translation.AssembleiaAssociadoRoute.pautas.paginacao(
                current,
                total
              )
            ),
          }}
        />
      )}
      {type === 'default' &&
        pages.map((item) => (
          <Button
            variant='text'
            key={item}
            onClick={() => onChange(item - 1)}
            disabled={item - 1 === current}
            color='primary'
            className={classes.paginacaoNumber}
          >
            {item}
          </Button>
        ))}
      {current < total && (
        <IconButton onClick={() => onChange(current + 1)} color='primary'>
          <DropdownIcon className={classes.paginacaoNext} />
        </IconButton>
      )}
    </nav>
  );
};

interface IconStatusProps {
  status: AssembleiaPautaStatus;
}

export const IconStatus: FC<IconStatusProps> = ({ status }) => {
  if (status === AssembleiaPautaStatus.Liberada) {
    return <CircleCheck noFill />;
  }
  if (status === AssembleiaPautaStatus.Encerrada) {
    return <CircleTimes />;
  }
  return <LockIcon />;
};
